import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brand } from '../components/common/Brand';
import { Seo } from '../components/Seo';
import './Landing.css';

// Ordered list of updates, newest first. No dates — just what changed, by
// platform. Add new entries at the top as features ship.
type Entry = { tag: string; title: string; points: string[] };

const LOG: Entry[] = [
  {
    tag: 'iPhone · Android · Desktop',
    title: 'Messaging — and rock-solid on VPN',
    points: [
      'Private device-to-device messaging: send notes, photos, and files straight between your own devices, with reactions, replies, read receipts, and typing indicators — never through a server.',
      'A clear VPN indicator on the Devices tab so you can see at a glance which devices are on your tunnel.',
      'Messaging and remote access are far more reliable while on a VPN — sends no longer stall, and your devices reconnect fast.',
    ],
  },
  {
    tag: 'iPhone · Android',
    title: "Every device's files, from your phone",
    points: [
      'Browse the shared folders on any of your online devices right from the Files tab — your home PC from your phone, your laptop from the couch.',
      'Select multiple files at once to download, upload, or delete — and pull a whole folder down as a single archive.',
      'Per-folder permissions you set and enforce on each device (read-only or read & write).',
    ],
  },
  {
    tag: 'iPhone · Android',
    title: 'Reach your devices anywhere',
    points: [
      'A built-in WireGuard client with saved connections — tap once to bring your devices onto a secure tunnel to a server you control (your router, your UniFi, your own WireGuard box).',
      'Your files, backups, and messages become reachable from anywhere — still with no third-party cloud in the middle.',
    ],
  },
  {
    tag: 'Web · Apps',
    title: 'Organize and manage your fleet',
    points: [
      'Group your devices into Sites (Home, Office) and rename them so your fleet is easy to read at a glance.',
      'Pause or remove any device from your account at any time, from the web dashboard.',
    ],
  },
  {
    tag: 'Desktop',
    title: 'Network shares & multi-select',
    points: [
      'Browse, map to a drive letter, and pin Windows and macOS network shares (SMB) right from the desktop app.',
      'Select multiple files at once — hold Shift to grab a range — to download or upload in one go.',
    ],
  },
  {
    tag: 'iPhone · Android · Desktop',
    title: 'Automatic Backup',
    points: [
      'Pick a folder and a schedule — LACloud quietly zips it and sends a full-quality copy to another of your own devices, all on its own.',
      'Phones back up over Wi-Fi while charging, so it simply happens overnight. No cloud middleman, nothing downscaled.',
    ],
  },
];

export const WhatsNew: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll('.lac .reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const ld = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: "What's New — Local Area Cloud",
    description: 'Recent updates to Local Area Cloud: messaging, cross-device file browsing, a built-in WireGuard VPN, device management, and automatic backup.',
    url: 'https://localareacloud.com/whats-new',
    isPartOf: { '@type': 'WebSite', name: 'Local Area Cloud', url: 'https://localareacloud.com' },
  };

  return (
    <div className="lac">
      <Seo
        title="What's New — Local Area Cloud"
        description="See what's new in Local Area Cloud: device-to-device messaging, cross-device file browsing, a built-in WireGuard VPN, device management, and scheduled automatic backup."
        path="/whats-new"
        jsonLd={ld}
      />
      <a className="skip" href="#top">Skip to content</a>

      {/* NAV */}
      <div className="nav">
        <div className="wrap nav-in">
          <a className="brand" href="/"><Brand variant="lockup" height={28} /></a>
          <nav className="nav-links">
            <a href="/#features">Features</a>
            <a href="/#pricing">Pricing</a>
            <Link to="/whats-new" aria-current="page">What's new</Link>
          </nav>
          <div className="nav-cta">
            <Link className="signin" to="/login">Sign in</Link>
            <a className="btn btn-primary" href="/#download">Download</a>
          </div>
          <button className="burger" aria-label="Menu" onClick={() => setMenuOpen((v) => !v)}>☰</button>
        </div>
        {menuOpen && (
          <div className="wrap" style={{ paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <a href="/#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="/#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <a href="/#download" onClick={() => setMenuOpen(false)}>Download</a>
            <Link to="/login" onClick={() => setMenuOpen(false)}>Sign in</Link>
          </div>
        )}
      </div>

      {/* HEADER */}
      <section className="hero" id="top" style={{ paddingBottom: 'clamp(30px,5vw,60px)' }}>
        <div className="aurora"><i className="a" /><i className="b" /><i className="c" /></div>
        <div className="wrap" style={{ position: 'relative', zIndex: 1, maxWidth: 780 }}>
          <p className="eyebrow">Product updates</p>
          <h1 style={{ fontSize: 'clamp(34px,5vw,58px)', lineHeight: 1.06 }}>What's new in<br /><span className="em">Local Area Cloud.</span></h1>
          <p className="lead" style={{ marginTop: 22 }}>Local Area Cloud is actively built and shipping. Here's what we've added lately —
            across iPhone, Android, and the desktop apps. One account, one private cloud, getting better every month.</p>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="band" style={{ paddingTop: 'clamp(30px,5vw,56px)' }}>
        <div className="wrap">
          <div className="log">
            {LOG.map((e, i) => (
              <div className="logitem reveal" key={i}>
                <div className="logmeta">
                  <span className="logtag">{e.tag}</span>
                </div>
                <h3>{e.title}</h3>
                <ul>
                  {e.points.map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>

          <div className="center reveal" style={{ maxWidth: 560, marginTop: 20 }}>
            <p className="lead" style={{ margin: '0 auto 26px' }}>Ready to make your own devices talk to each other?</p>
            <div className="hero-cta" style={{ justifyContent: 'center' }}>
              <a className="btn btn-primary" href="/#download">↓&nbsp; Get Local Area Cloud</a>
              <Link className="btn btn-ghost" to="/">Back to home</Link>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap foot-cases">
          <span className="foot-cases-h">Popular uses</span>
          <Link to="/airdrop-for-windows">AirDrop for Windows</Link>
          <Link to="/transfer-files-phone-to-pc">Transfer files between phone &amp; PC</Link>
          <Link to="/local-network-file-sharing">Local network file sharing</Link>
          <Link to="/file-sharing-without-the-cloud">File sharing without the cloud</Link>
        </div>
        <div className="wrap foot-in">
          <a className="brand" href="/"><Brand variant="lockup" height={28} /></a>
          <span style={{ color: 'var(--faint)' }}>© 2026 Chrono Travel LLC · Local Area Cloud. Your files, your network.</span>
          <div className="foot-links">
            <a href="/#pricing">Pricing</a>
            <Link to="/whats-new">What's new</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WhatsNew;
